import { useMemo } from "react";

import { vCPUbyInstanceType } from "@/components/instance-types";
import { useAvailableRunners } from "@/hooks/use-available-runners";
import { useListEnvironmentClasses, type PlainRunnerEnvironmentClass } from "@/queries/environment-queries";
import type { PlainProjectEnvironmentClass } from "@/queries/project-queries";
import { type PlainRunner } from "@/queries/runner-queries";
import { RunnerKind } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { RunnerPhase, RunnerCapability, type Runner } from "gitpod-next-api/gitpod/v1/runner_pb";

export type EnvironmentClassEntry = {
    clazz: PlainRunnerEnvironmentClass;
    runner: PlainRunner;

};

/**
 * Maps environment class ID to the corresponding environment class entry
 */
export type EnvironmentClassMap = Record<string, EnvironmentClassEntry>;

export function convertToProjectEnvironmentClass(
    entry: EnvironmentClassEntry,
    order: number,
): PlainProjectEnvironmentClass {
    return {
        environmentClass: { case: "environmentClassId" as const, value: entry.clazz.id },
        order,
    } as PlainProjectEnvironmentClass;
}

export function useEnvironmentClassEntries({ enabled }: { enabled: boolean } = { enabled: true }) {
    const { data: environmentClasses, isLoading: isLoadingClasses } = useListEnvironmentClasses({
        filter: { enabled },
    });
    const { availableRunners, isLoading: isLoadingRunners } = useAvailableRunners({ enabled });

    const isLoading = isLoadingClasses || isLoadingRunners;

    const environmentClassEntries = useMemo(() => {
        const classes = environmentClasses?.classes;
        if (classes === undefined || isLoadingRunners || isLoadingClasses) {
            return [];
        }

        const runnersMap = new Map(availableRunners.map((r) => [r.runnerId, r]));
        const joined: EnvironmentClassEntry[] = [];
        for (const clazz of classes) {
            const runner = runnersMap.get(clazz.runnerId);
            if (runner) {
                // Filter out local/desktop runners — no longer supported
                if (runner.kind === RunnerKind.LOCAL) {
                    continue;
                }

                joined.push({ clazz, runner });
            }
        }

        return joined;
    }, [availableRunners, environmentClasses, isLoadingClasses, isLoadingRunners]);

    return {
        isLoading,
        environmentClassEntries,
    };
}

/**
 * Queries all available environment classes, then store them in a Record
 * that maps environment class ID to the corresponding environment class
 */
export function useEnvironmentClassMap() {
    const { data: environmentClasses, isLoading: isLoadingClasses } = useListEnvironmentClasses({
        filter: { enabled: true },
    });
    const { availableRunners, isLoading: isLoadingRunners } = useAvailableRunners();

    const isLoading = isLoadingClasses || isLoadingRunners;

    const environmentClassMap = useMemo(() => {
        const classes = environmentClasses?.classes;
        if (classes === undefined || isLoadingRunners || isLoadingClasses) {
            return {};
        }

        const runnersMap = new Map<string, Runner>();
        for (const runner of availableRunners) {
            runnersMap.set(runner.runnerId, runner);
        }

        const classMap: EnvironmentClassMap = {};
        for (const clazz of classes) {
            const runner = runnersMap.get(clazz.runnerId);
            if (runner) {
                // Filter out local/desktop runners — no longer supported
                if (runner.kind === RunnerKind.LOCAL) {
                    continue;
                }

                classMap[clazz.id] = { clazz, runner };
            }
        }

        return classMap;
    }, [availableRunners, environmentClasses, isLoadingClasses, isLoadingRunners]);

    return {
        isLoading,
        environmentClasses: environmentClassMap,
    };
}

export function getCompareLabel(entry: EnvironmentClassEntry) {
    const label =
        `${entry?.runner?.name || ""},${entry?.clazz?.displayName}, ${entry?.clazz?.description}`.toLowerCase();
    return label;
}

export function getEnvironmentClassStatus(environmentClassEntry: EnvironmentClassEntry) {
    const offline =
        environmentClassEntry?.runner?.status?.phase !== RunnerPhase.ACTIVE &&
        environmentClassEntry?.runner?.status?.phase !== RunnerPhase.DEGRADED;
    const degraded = environmentClassEntry?.runner?.status?.phase === RunnerPhase.DEGRADED;
    return { offline, degraded };
}

/**
 * Check if an environment class entry supports a specific runner capability.
 * Handles both numeric enum values and string enum names (e.g., "RUNNER_CAPABILITY_AGENT_EXECUTION").
 */
export function hasRunnerCapability(entry: EnvironmentClassEntry, capability: RunnerCapability): boolean {
    const capabilities = entry.runner.status?.capabilities;
    if (!capabilities) {
        return false;
    }

    // Check if the capability exists as a numeric value
    if (capabilities.includes(capability)) {
        return true;
    }

    // Check if the capability exists as a string name (e.g., "RUNNER_CAPABILITY_AGENT_EXECUTION")
    // This handles cases where toPlainMessage converts enum values to their string names
    const capabilityName = RunnerCapability[capability];
    return capabilities.some(
        (cap) => (cap as unknown) === capabilityName || (cap as unknown) === `RUNNER_CAPABILITY_${capabilityName}`,
    );
}

/**
 * Filter environment class entries to only include those that support the specified runner capabilities.
 */
export function filterByRunnerCapabilities(
    entries: EnvironmentClassEntry[],
    requiredCapabilities: RunnerCapability[],
): EnvironmentClassEntry[] {
    return entries.filter((entry) =>
        requiredCapabilities.every((capability) => hasRunnerCapability(entry, capability)),
    );
}

/**
 * Sort environment class entries by availability, degraded status, and CPU.
 *
 * First level of ordering:
 * 1. Active runners
 * 2. Degraded runners
 * 3. Offline runners
 *
 * Second level of ordering:
 * 1. CPU (if available)
 * 2. Label
 */
export function sortEnvironmentClassEntries(entries: EnvironmentClassEntry[]): EnvironmentClassEntry[] {
    const getCPU = (entry: EnvironmentClassEntry) => {
        const instanceType = entry.clazz.configuration.find((c) => c.key === "instanceType")?.value;
        return instanceType ? vCPUbyInstanceType[instanceType] : undefined;
    };
    const isAvailable = (entry: EnvironmentClassEntry) => {
        const { offline } = getEnvironmentClassStatus(entry);
        return !offline;
    };
    const isDegraded = (entry: EnvironmentClassEntry) => {
        const { degraded } = getEnvironmentClassStatus(entry);
        return degraded;
    };
    return entries.sort((a, b) => {
        // Order by availability
        if (isAvailable(a) && !isAvailable(b)) {
            return -1;
        }
        if (!isAvailable(a) && isAvailable(b)) {
            return 1;
        }
        // Order by degraded status
        if (!isDegraded(a) && isDegraded(b)) {
            return -1;
        }
        if (isDegraded(a) && !isDegraded(b)) {
            return 1;
        }
        // Order active ones by CPU if available
        const aCPU = getCPU(a);
        const bCPU = getCPU(b);
        if (aCPU && bCPU) {
            return aCPU - bCPU;
        }
        // Fallback to the label if the CPU is not available
        const aLabel = getCompareLabel(a);
        const bLabel = getCompareLabel(b);
        return aLabel.localeCompare(bLabel);
    });
}
